import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useStore } from "../../../helpers";
import React, { useEffect, useMemo } from "react";
import { RNBoothSelector } from "../../../components/form-components";
import { FTPAutoComplete, FTPInput } from "../../../components-new";
import { AddButton, DeleteButton } from "./Buttons";
import { FormButtons, LoadingState } from "../../../components";
import { toJS } from "mobx";
import { UserAutoComplete } from "./UserAutoComplete";
import useUIFormFunctions from "./hooks/useUIFormFunctions";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { UIFormValidationSchema } from "./hooks/schema";

const UIForm = () => {
    const {
        taskManagerStore: {
            toggle_assign_task_modal,
            users,
            bulk_assign_task_list,
        },
    } = useStore();
    const methods = useForm({
        defaultValues: {
            survey: null,
            tasks: [{ booth: null, phone: null, sample_size: 0 }],
        },
        resolver: yupResolver(UIFormValidationSchema),
    });
    useMemo(() => {
        if (bulk_assign_task_list.length > 0) {
            methods.reset({
                survey: null,
                tasks: toJS(bulk_assign_task_list),
            });
        }
    }, []);
    const [loading, setLoading] = React.useState(false);

    const { surveys, error, isLoading, onError, onSubmit } =
        useUIFormFunctions();
    const { fields, append, remove } = useFieldArray({
        control: methods.control,
        name: "tasks",
    });

    if (isLoading || loading) {
        return <LoadingState />;
    }
    if (error) {
        toast.error("Something went wrong while fetching surveys");
        return null;
    }

    return (
        <div>
            <FormProvider {...methods}>
                <>
                    <FTPAutoComplete
                        label={"Survey"}
                        name={`survey`}
                        placeholder={"Enter survey name"}
                        data={[...surveys]}
                        lookupKey="surveyname"
                    />
                    <br />

                    <div className="hidden md:block">
                        <table className="w-full mb-4 border table-fixed">
                            <thead>
                                <tr>
                                    <th
                                        colSpan={2}
                                        className="border p-2 text-left"
                                    >
                                        Booth
                                    </th>
                                    <th
                                        colSpan={2}
                                        className="border p-2 text-left"
                                    >
                                        Assignee
                                    </th>
                                    <th className="border p-2 text-left">
                                        Sample Size
                                    </th>
                                    <th className="border p-2 text-left">
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {fields.map((field, index) => (
                                    <tr key={field.id}>
                                        <td colSpan={2} className="border p-2">
                                            <RNBoothSelector
                                                name={`tasks.${index}.booth`}
                                                isMulti={false}
                                                selector="display"
                                            />
                                        </td>

                                        <td colSpan={2} className="border p-2">
                                            <UserAutoComplete
                                                name={`tasks.${index}.phone`}
                                                data={toJS(users)}
                                                placeholder="Assignee"
                                                lookupKey="name"
                                                addNewOption={false}
                                            />
                                        </td>

                                        <td className="border p-2">
                                            <FTPInput
                                                name={`tasks.${index}.sample_size`}
                                                type="number"
                                            />
                                        </td>

                                        <td className="border p-2">
                                            <DeleteButton
                                                onClick={() => remove(index)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="md:hidden space-y-3">
                        {fields.map((field, index) => (
                            <div
                                key={field.id}
                                className="border rounded-lg p-3 grid gap-3"
                            >
                                <RNBoothSelector
                                    name={`tasks.${index}.booth`}
                                    isMulti={false}
                                    selector="display"
                                />

                                <UserAutoComplete
                                    name={`tasks.${index}.phone`}
                                    data={toJS(users)}
                                    placeholder="Assignee"
                                    lookupKey="name"
                                    addNewOption={false}
                                />

                                <FTPInput
                                    name={`tasks.${index}.sample_size`}
                                    type="number"
                                />

                                <div className="flex justify-end">
                                    <DeleteButton
                                        onClick={() => remove(index)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <AddButton
                        onClick={() =>
                            append({ booth: null, phone: null, sample_size: 0 })
                        }
                    />
                    <FormButtons
                        onSave={async () => {
                            setLoading(true);
                            await methods.handleSubmit(onSubmit, onError)();
                            setLoading(false);
                            toggle_assign_task_modal();
                        }}
                        onCancel={() => {
                            toggle_assign_task_modal();
                        }}
                    />
                </>
            </FormProvider>
        </div>
    );
};

export default UIForm;
